import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Redirect } from 'react-router';
import axios from '../../helpers/axios';
import { isUatEnvironment } from '../../helpers/uatMasking';
import { ToastContainer, toast } from 'react-toastify';
import { Grid, Card, CardContent, CardHeader, Button, Typography, Dialog, DialogTitle, DialogActions, Backdrop, CircularProgress, Stack, Chip, Box } from '@mui/material';
import { withTranslation, WithTranslation } from 'react-i18next';
import PaymentsHeader from './PaymentsHeader';
import { DataGrid, GridRowParams, GridCellParams } from '@mui/x-data-grid';

import EditOutlined from '@mui/icons-material/EditOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

export interface IProps {
    user: {
        id: number;
        name: string;
        email: string;
        role: string;
        is_active: boolean;
        selectedHierarchyIds: any;
    };
}

export interface IState {
    pendingReviewTransactions: any;
    showLoader: boolean;
    showDialogBox: boolean;
    rowInformation: any;
    reviewStatus: any;
}

class TransactionsPendingApproval extends React.Component<RouteComponentProps<any> & IProps & WithTranslation, IState> {
    constructor(props: RouteComponentProps & IProps & WithTranslation) {
        super(props);
        this.state = {
            pendingReviewTransactions: [],
            showLoader: false,
            showDialogBox: false,
            rowInformation: null,
            reviewStatus: '',
        }
    }
    public componentDidMount(): void {
        this.fetchPendingReviewTransactions();
    }

    public fetchPendingReviewTransactions = () => {
        this.setState({ showLoader: true });
        axios.get('pendingReviewTransactions', { params: { "hierarchies": this.props.user.selectedHierarchyIds } }).then((resp: any) => {
            if (resp.data.success) {
                this.setState({ showLoader: false });
                this.setState({
                    pendingReviewTransactions: resp.data.data?.map((el: any) => {
                        return {
                            "id": el.id,
                            "user_id": el.user_id,
                            "name": el.name,
                            "phone": el.phone,
                            "transfer_id": el.transfer_id,
                            "amount": el.amount,
                            "beneficiary_type": el.beneficiary_type,
                            "beneficiary": (el.beneficiary_type == "upi") ? el.upi_key : ((el.beneficiary_type == "paytm") ? el.paytm_phone : el.bank_ac_number),
                            "requested_on": el.requested_on
                        }
                    })
                })
            } else {
                this.setState({ showLoader: false });
                toast.error(resp.data.msg, {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored"
                });
                let errors = resp.data.errors;
                for (var error in errors) {
                    toast.error(errors[error][0], {
                        position: "top-right",
                        autoClose: 3000,
                        hideProgressBar: true,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "colored"
                    });
                }
            }
        }).catch((err) => console.log("err", err))
    }

    public handleReviewTransaction = (row: any, reviewStatus: any) => {
        this.setState({ showLoader: true });
        let data = {
            "transaction_id": row.id,
            "status": (reviewStatus == "Approve") ? "Approved" : "Rejected",
        }
        axios.post('reviewTransaction', data, { headers: { "content-Type": "application/json" } }).then((response: any) => {
            if (response.data.success) {
                this.setState({ showLoader: false });
                toast.success(response.data.msg, {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored"
                });
                this.fetchPendingReviewTransactions();
                this.setState({ rowInformation: null, reviewStatus: '', showDialogBox: false });
            } else {
                this.setState({ showLoader: false });
                toast.error(response.data.msg, {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored"
                });
                let errors = response.data.errors;
                for (var error in errors) {
                    toast.error(errors[error][0], {
                        position: "top-right",
                        autoClose: 3000,
                        hideProgressBar: true,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "colored"
                    });
                }
                this.setState({ rowInformation: null, reviewStatus: '', showDialogBox: false });
            }
        })
    }

    public render() {
        if (!localStorage.getItem('token')) {
            return (
                <Redirect to="/login" />
            );
        }
        const { t } = this.props;
        const renderMaskedCell = (value: React.ReactNode) => (
            <Box
                component="span"
                sx={isUatEnvironment ? {
                    filter: 'blur(4px)',
                    opacity: 0.5,
                    userSelect: 'none',
                } : undefined}
            >
                {value}
            </Box>
        );

        const columns = [
            {
                headerName: "UID",
                field: "user_id",
                width: 80,
                renderCell: (params: GridCellParams) => (
                    <Typography
                        sx={{ cursor: 'pointer' }}
                        variant="body2"
                        onClick={(e) => {
                            e.stopPropagation();
                            window.open('/neighbourhood/customers/' + params.row.user_id, '_blank');
                        }}
                        component={"span"}
                    >
                        {params.row.user_id}
                    </Typography>
                )
            },
            {
                headerName: "Name",
                field: "name",
                width: 150,
                renderCell: (params: GridCellParams) =>
                    renderMaskedCell(params.value),
            },
            {
                headerName: "Phone",
                field: "phone",
                width: 100,
                renderCell: (params: GridCellParams) =>
                    renderMaskedCell(params.value),
            },
            {
                headerName: "Transaction ID",
                field: "id",
                width: 110,
                renderCell: (params: GridCellParams) =>
                    renderMaskedCell(params.value),
            },
            {
                headerName: "Transfer Id",
                field: "transfer_id",
                width: 120,
                renderCell: (params: GridCellParams) =>
                    renderMaskedCell(params.value),
            },
            {
                headerName: "Amount",
                field: "amount",
                type: "number",
                width: 100,
                renderCell: (params: GridCellParams) =>
                    renderMaskedCell(params.value),
            },
            {
                headerName: "Beneficiary",
                field: "beneficiary",
                width: 200,
                renderCell: (params: GridCellParams) => (
                    <Typography variant='body2'>
                        {renderMaskedCell(params.row.beneficiary)} <Chip size="small" label={params.row.beneficiary_type} sx={{ textTransform: "capitalize" }} color='info' />
                    </Typography>
                )
            },
            {
                headerName: "Requested On",
                field: "requested_on",
                width: 150,
            },
            {
                headerName: "Actions",
                field: "actions",
                type: "actions",
                width: 200,
                renderCell: (params: GridCellParams) => (
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            size="small"
                            className="float-right"
                            tabIndex={params.hasFocus ? 0 : -1}
                            onClick={() => this.setState({ showDialogBox: true, rowInformation: params.row, reviewStatus: 'Approve' })}
                        >
                            Approve
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            className="float-right"
                            tabIndex={params.hasFocus ? 0 : -1}
                            onClick={() => this.setState({ showDialogBox: true, rowInformation: params.row, reviewStatus: 'Reject' })}
                        >
                            Reject
                        </Button>
                    </Stack>
                )
            }
        ]

        return (
            <div>
                <Backdrop
                    sx={{ zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                    open={this.state.showLoader}
                >
                    <CircularProgress className='customLoader' />
                </Backdrop>
                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={true}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
                <PaymentsHeader user={this.props.user}/>
                <Grid container spacing={2}>
                    <Grid item md={12} xs={12} className="mt-3">
                        <Card>
                            <CardHeader
                                title="Transactions Pending Approval List"
                                className="text-left"
                            />

                            <CardContent>
                                <DataGrid
                                    aria-label="Transactions Pending Approval List"
                                    columns={columns}
                                    rows={this.state.pendingReviewTransactions}
                                    autoHeight={true}
                                    components={{
                                        MoreActionsIcon: EditOutlined,
                                    }}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Dialog
                    open={this.state.showDialogBox}
                    onClose={() => this.setState({ showDialogBox: false })}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">
                        {
                            (this.state.reviewStatus == "Approve") &&
                            <Stack direction={"column"} sx={{ alignItems: "center" }}>
                                <CheckCircleRoundedIcon sx={{ fontSize: "5rem" }} color='success' />
                                <Typography>Are you sure ? You want to <span style={{ color: "green", fontWeight: "700" }}>Approve</span> this Transaction</Typography>
                            </Stack>
                        }
                        {
                            (this.state.reviewStatus == "Reject") &&
                            <Stack direction={"column"} sx={{ alignItems: "center" }}>
                                <CancelRoundedIcon sx={{ fontSize: "5rem" }} color='error' />
                                <Typography>Are you sure ? You want to <span style={{ color: "red", fontWeight: "700" }}>Reject</span> this Transaction</Typography>
                            </Stack>
                        }
                    </DialogTitle>
                    <DialogActions>
                        <Button variant='contained' className='customCloseButton' onClick={() => this.setState({ showDialogBox: false, reviewStatus: '', rowInformation: null })}>{t("Cancel")}</Button>
                        <Button variant='contained' onClick={() => this.handleReviewTransaction(this.state.rowInformation, this.state.reviewStatus)} autoFocus>{t("Yes")}</Button>
                    </DialogActions>
                </Dialog>
            </div>
        )
    }
}

export default withRouter(withTranslation()(TransactionsPendingApproval))
